/**
 * Phase 10 — VAPID push subscription helpers.
 *
 * Lifecycle:
 *  1. Browser must support Notification API + Push Manager + service worker.
 *  2. We fetch the public VAPID key from /saas/notifications/vapid-public-key.
 *  3. Service worker registration is reused (registered by index.js); we wait
 *     until it's `ready`.
 *  4. Subscribe via PushManager.subscribe with the converted public key.
 *  5. POST the subscription JSON to backend (also pass marketing_opt_in).
 *
 * All errors are returned as { ok: false, error } so the calling component
 * can surface a user-friendly message.
 */
import api from "@/lib/api";

const PERMISSION_KEY = "lumen.push.permission_asked";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager"   in window &&
    "Notification"  in window
  );
}

export function permissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export function hasAskedBefore() {
  return localStorage.getItem(PERMISSION_KEY) === "1";
}

export function markAsked() {
  localStorage.setItem(PERMISSION_KEY, "1");
}

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Subscribe the current browser to push notifications and forward the
 * subscription to the backend. Optionally toggles marketing_opt_in on the
 * customer record at the same time.
 */
export async function subscribePush({ token, marketingOptIn = false } = {}) {
  if (!isPushSupported()) return { ok: false, error: "Tarayıcınız bildirimleri desteklemiyor." };

  // 1. Permission
  const perm = await Notification.requestPermission();
  markAsked();
  if (perm !== "granted") return { ok: false, error: "Bildirim izni verilmedi." };

  // 2. SW ready
  let reg;
  try {
    reg = await navigator.serviceWorker.ready;
  } catch (e) {
    return { ok: false, error: "Service worker bulunamadı." };
  }

  // 3. VAPID key
  let vapidKey;
  try {
    const res = await api.get("/saas/notifications/vapid-public-key");
    vapidKey = res.data.public_key;
  } catch (e) {
    return { ok: false, error: "VAPID anahtarı alınamadı." };
  }

  // 4. Subscribe
  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }
  } catch (e) {
    return { ok: false, error: "Push aboneliği başarısız: " + (e?.message || "?") };
  }

  // 5. POST to backend
  try {
    await api.post(
      "/saas/notifications/subscribe",
      {
        subscription:     sub.toJSON(),
        marketing_opt_in: !!marketingOptIn,
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {},
    );
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || "Sunucuya kaydedilemedi." };
  }
  return { ok: true };
}

export async function unsubscribePush({ token } = {}) {
  if (!isPushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return { ok: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true, removed: 0 };
  await sub.unsubscribe().catch(() => null);
  try {
    await api.post(
      "/saas/notifications/unsubscribe",
      { endpoint: sub.endpoint },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {},
    );
  } catch { /* ignore */ }
  return { ok: true, removed: 1 };
}
